import { defineRule } from "vee-validate";
import {
  required,
  email,
  regex,
  min,
  max,
  numeric,
  integer,
  alpha_dash
} from "@vee-validate/rules";
import dayjs from "dayjs";

function removePrefix(prefix_name) {
  return prefix_name?.replace(/[^_]*_/, "");
}

defineRule("alpha_num", (value, [fieldName]) => {
  if (value && !alpha_dash(value)) {
    return `${fieldName} must only contain alphabetical letters and numbers.`;
  }
  return true;
});

defineRule("regex", (value, [regexExpression, fieldName, message]) => {
  if (value && !regex(value, { regex: regexExpression })) {
    return message || `${fieldName} is invalid.`;
  }
  return true;
});

defineRule("passwordRegex", (value, [regexExpression, fieldName]) => {
  if (value && !regex(value, { regex: regexExpression })) {
    return `${fieldName}  must be stronger.`;
  }
  return true;
});

defineRule("postalCodeRegex", (value, [regexExpression]) => {
  if (value && !regex(value, { regex: regexExpression })) {
    return "Please enter a valid postal code in the following format A1A 2B2.";
  }
  return true;
});

defineRule("postalCodeRegexUSA", (value, [regexExpression]) => {
  if (value && !regex(value, { regex: regexExpression })) {
    return "Please enter a valid zip code in the following format 12345.";
  }
  return true;
});

defineRule("required", (value, [fieldName, message]) => {
  if (!required(value)) {
    if (message) return message;
    return `${removePrefix(fieldName)} must be provided.`;
  }
  return true;
});

defineRule("requiredSectionView", (value, [fieldName]) => {
  if (!required(value)) {
    return `Please review comments for ${removePrefix(fieldName)}.`;
  }
  return true;
});

defineRule("email", (value, [fieldName]) => {
  if (value && !email(value)) {
    return `${fieldName} is invalid.`;
  }
  return true;
});

defineRule("min", (value, [fieldName, length]) => {
  if (value && !min(value, { length })) {
    return `${fieldName} minimum length must be of ${length} characters.`;
  }
  return true;
});

defineRule("max", (value, [fieldName, length]) => {
  if (value && !max(value, { length })) {
    return `${fieldName} cannot exceed ${length} characters.`;
  }
  return true;
});

defineRule("maxLimit", (value, [fieldName, maxLimit, unit]) => {
  if (value && value > maxLimit) {
    return `${fieldName} length cannot exceed ${maxLimit} ${unit}`;
  }
  return true;
});

defineRule("minLimit", (value, [fieldName, minLimit, unit]) => {
  if (value && value < minLimit) {
    return `${fieldName} length must be atleast ${minLimit} ${unit}`;
  }
  return true;
});

defineRule("streetNumberInvalid", value => {
  if (value) {
    return `You must enter a valid street number.`;
  }
  return true;
});

defineRule("validDate", (value, [dateRegex, dateFormat]) => {
  const valid = dayjs(value, dateFormat).format(dateFormat) === value;
  if (!value) {
    return true;
  }
  if (!(dateRegex.test(value) && valid)) {
    return `Please enter a valid date in ${dateFormat} format.`;
  }

  return true;
});

defineRule("minDate", (value, [minDate, errorMessage, dateFormat]) => {
  let date =
    minDate !== undefined && minDate !== null ? dayjs(minDate) : dayjs();
  let valueDate = dayjs(value, dateFormat);
  if (
    value &&
    !(date.isSame(valueDate, "day") || date.isBefore(valueDate, "day"))
  ) {
    return errorMessage;
  }
  return true;
});

defineRule("maxDate", (value, [maxDate, errorMessage]) => {
  if (
    value &&
    maxDate &&
    !(dayjs(value).isBefore(maxDate) || dayjs(value).isSame(maxDate))
  ) {
    return errorMessage;
  }
  return true;
});

defineRule("dateWeekDayOnly", (value, [dateFormat]) => {
  let whichDay = dayjs(value, dateFormat).day();
  if (!(whichDay === 6 || whichDay === 0)) {
    return "Sorry, you cannot choose a date which occurs on a weekend.";
  }
  return true;
});

defineRule("numeric", value => {
   if (value && !numeric(value)) {
    return "Please enter a valid number";
  }
  const hasMoreThanOneDecimal = (value?.match(/\./g) || []).length > 1;
  if (!hasMoreThanOneDecimal && value?.includes(".")) {
    value = value?.replace(/\./g, "");
  }
 
  return true;
});

defineRule("integer", value => {
  if (value && !integer(value)) {
    return "Please enter a valid number";
  }
  return true;
});

defineRule("phoneNumber", value => {
  if (
    value &&
    // eslint-disable-next-line
    !/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4}?[,]?[0-9]{0,5}$/.test(
      value
    )
  ) {
    return "Please enter a valid phone number";
  }
  return true;
});

defineRule("futureDateNotAllowed", (value, [fieldName]) => {
  if (value && dayjs(value).isAfter(dayjs().format("YYYY-MM-DD"))) {
    return `${fieldName} can not be a future date`;
  }
  return true;
});

defineRule("streetNameInvalid", (value, [isInvalid, errorMessage]) => {
  if (value && isInvalid)
    return errorMessage || "Please enter a valid street address.";
  return true;
});

defineRule("required_if", (value, [name, valid]) => {
  if (!valid && !required(value)) {
    return `${removePrefix(name)} must be provided.`;
  }
  return true;
});

defineRule("isChecked", (value, [fieldName, message]) => {
  if (!value) {
    return message || `${fieldName} is not valid.`;
  }
  return true;
});
defineRule("limitRestrictedCharacters", (value, [fieldName]) => {
  if (!restrictedCharacter(value).valid) {
    return `${fieldName} contains restricted character`;
  }
  return true;
});

defineRule("limitLicensePlateRestrictedCharacters", (value, [fieldName]) => {
  const regex = /^[a-zA-Z0-9]+$/;
  if (!regex.test(value)) {
    return `${fieldName} contains restricted character.`;
  }
  return true;
});

defineRule("quantity", value => {
  if (!value || value == 0) return "Quantity should be greater than zero";
  return true;
});

defineRule("isValidTime", (value, [isValid, message]) => {
  if (isValid) return message;
  return true;
});

export const restrictedCharacter = (value, inputField) => {
  const resrtictedCharacters = [
    "%",
    "+",
    "/",
    "@",
    "!",
    "#",
    "^",
    "&",
    "?",
    ":",
    "(",
    ")",
    "{",
    "}",
    "[",
    "]",
    "~",
    ";",
    "\\",
    "<",
    ">",   
    "$",
    "*",
    "="
  ];
  const hasRestrictedCharacter = resrtictedCharacters.map(
    restrictedCharacter => {
      if (value && typeof value === "string") {
        if (inputField === "email") {
          const emailValue = value.replace("@", "");
          return emailValue.includes(restrictedCharacter) ? true : false;
        } else {
          return value.includes(restrictedCharacter) ? true : false;
        }
      }
    }
  );
  return { valid: hasRestrictedCharacter.includes(true) ? false : true };
};

export const licensePlateRestrictedCharacter = value => {
  const resrtictedCharacters = [
    "%",
    "+",
    "/",
    "@",
    "!",
    "#",
    "^",
    "&",
    "?",
    ":",
    ",",
    "(",
    ")",
    "{",
    "}",
    "[",
    "]",
    "~",
    ";",
    "\\",
    "<",
    ">",
    "-",
    " ",
    "`",
    "$",
    "*",
    "_",
    "="
  ];
  const hasRestrictedCharacter = resrtictedCharacters.map(
    restrictedCharacter => {
      if (value && typeof value === "string") {
        return value.includes(restrictedCharacter) ? true : false;
      }
    }
  );
  return { valid: hasRestrictedCharacter.includes(true) ? false : true };
};

defineRule("addressFormat", (value, [fieldName]) => {
  if (value && (!/[a-zA-Z]/.test(value) || !/\d/.test(value))) {
    return "Please enter a valid street address.";
  }
  return true;
});

